import moment from 'moment-timezone-all'
import { createSelector } from 'reselect'

import { selectAccountPreferredReportsAverage } from 'selectors/app/base'
import { selectById as selectKbsById } from 'subapps/kb/selectors/knowledge_bases'
import { any, compact } from 'util/arrays'
import { relativeToAbsoluteDate } from 'util/date'

function selectBase(state) {
  return state.reports.controls
}

export function selectActiveKnowledgeBaseId(state) {
  return state.location.query && state.location.query.knowledge_base_id
}

const selectActiveKnowledgeBaseFilter = createSelector(
  selectActiveKnowledgeBaseId,
  knowledgeBaseId => {
    if (knowledgeBaseId) {
      return {
        property_name: 'kb.id',
        operator: 'eq',
        property_value: knowledgeBaseId,
      }
    }
    return null
  }
)

export function selectActiveMailboxId(state) {
  return state.location.query && state.location.query.inbox_id
}

const selectActiveMailboxFilter = createSelector(
  selectActiveMailboxId,
  mailboxId => {
    if (mailboxId) {
      return {
        property_name: 'conversation.inbox.id',
        operator: 'eq',
        property_value: mailboxId,
      }
    }
    return null
  }
)

export function selectAreBusinessHoursEnabled(state) {
  const { areBusinessHoursEnabled = false } = selectBase(state)
  return !!areBusinessHoursEnabled
}

export function selectShowBusinessHoursNotice(state) {
  const { showBusinessHoursNotice = true } = selectBase(state)
  return showBusinessHoursNotice
}

export function selectAreControlsPending(state) {
  return !!selectFocusedInput(state)
}

export function selectFocusedInput(state) {
  return selectBase(state).focusedInput
}

export function selectTimezone(state) {
  return selectBase(state).timezone
}

export function selectTimezoneOffset(state) {
  return moment.tz(selectBase(state).timezone).utcOffset() / 60
}

export function selectBrowserTimezoneOffset() {
  return new Date().getTimezoneOffset() / -60
}

const selectTimeframeRaw = createSelector(selectBase, base => base.timeframe)
const selectTimeframeRawProcessed = createSelector(selectTimeframeRaw, raw => {
  if (typeof raw === 'string') return relativeToAbsoluteDate(raw)
  return raw
})

export const selectTimeframe = createSelector(
  selectTimeframeRawProcessed,
  timeframe => {
    const { end, start } = timeframe
    return {
      end: moment(end),
      start: moment(start),
    }
  }
)

export const selectTimeframeForQuery = createSelector(
  selectTimeframeRawProcessed,
  selectTimezone,
  (timeframe, timezone) => {
    const { end, start } = timeframe
    const momentStart = moment.tz(start, timezone).startOf('day')
    const startOffset = momentStart.utcOffset()
    return {
      end: moment
        .tz(end, timezone)
        .add(1, 'day')
        .startOf('day')
        .utcOffset(startOffset, true)
        .utcOffset(startOffset)
        .format(),
      start: momentStart.format(),
    }
  }
)

export function selectTimeframeEnd(state) {
  return selectTimeframe(state).end
}

export function selectTimeframeStart(state) {
  return selectTimeframe(state).start
}

export const selectKbRatingType = createSelector(
  selectActiveKnowledgeBaseId,
  selectKbsById,
  (kbId, byId) => {
    if (kbId && byId[kbId]) return byId[kbId].ratingsType
    return any(
      ({ ratingsType }) => ratingsType === 'emoji',
      Object.values(byId)
    )
      ? 'emoji'
      : 'upvote'
  }
)

export const selectPreviousPeriodOffset = createSelector(
  selectTimeframe,
  ({ end, start }) => {
    const mEnd = moment(end)
    return moment.duration(mEnd.diff(start)).add(1, 'day')
  }
)

const selectFiltersForQuery = createSelector(
  selectActiveKnowledgeBaseFilter,
  selectActiveMailboxFilter,
  (activeKnowledgeBaseFilter, activeMailboxFilter) => {
    return compact([activeKnowledgeBaseFilter, activeMailboxFilter])
  }
)

export const selectQueryBase = createSelector(
  selectAccountPreferredReportsAverage,
  selectAreBusinessHoursEnabled,
  selectFiltersForQuery,
  selectTimeframeForQuery,
  selectTimezone,
  (average, businessHours, filters, timeframe, timezone) => {
    return {
      average,
      businessHours,
      filters,
      timeframe,
      timezone,
    }
  }
)

export const selectScopedQueryBase = createSelector(
  (state, props) => props.id,
  (state, props) => props.propertyName,
  selectQueryBase,
  (id, propertyName, queryBase) => {
    const query = {
      ...queryBase,
      scope: { [propertyName]: id },
    }
    if (propertyName === 'conversation.assignee.id') {
      query.agentId = id
    }
    if (propertyName === 'conversation.assignee_team.id') {
      query.groupId = id
    }
    return query
  }
)

export const selectToday = createSelector(selectTimezone, timezone => {
  return moment(
    moment
      .tz(timezone)
      .startOf('day')
      .format('YYYY-MM-DD')
  )
})
